/**
 * ImpactStrip.tsx · 09_APP · OS-006 · 2026-10-20 · C48 (+ C47 inside the referral card), v67
 *
 * Her words: "people served this year or something like that. Those, how many referred, so that we have that right
 * in front of us." Ruling on the mockup: "This is only for women." Design approved: "Okay, looks good."
 * Built to 00_BRAIN/PROPOSALS/IMPACT_STRIP_MOCKUP_2026-10-20.html. Four cards, women only, year picker, a
 * "counted since" line so a number is never read as more complete than the capture behind it, and a tap-through
 * to the real, unduplicated list. Every number is os_impact(), a live count. Zero says zero.
 */

import { useEffect, useState } from "react";
import { os, type Chip, type Impact, type ImpactRow } from "../lib/os";
import { AskClaude, setAskContext } from "./AskClaude";
import { ReferralQuick } from "./Referral";
import { OutreachQuick } from "./Outreach";
import { onImpact } from "./ComingUp";
import "../impact.css";

type Card = "connected" | "referred" | "students" | "workshops" | "reached" | "report" | null;
const day = (iso: string | null | undefined) =>
  iso ? new Date(iso + (iso.length === 10 ? "T12:00:00" : "")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
const sinceLine = (since: string | null, year: number, none: string) => {
  if (!since) return none;
  const jan1 = `${year}-01-01`;
  return `counted since ${day(since > jan1 ? since : jan1)}`;
};
const REPORT_IDEAS = [
  "Women served in 2026, by ZIP code",
  "Referrals by agency this year",
  "Academy completion rate",
  "One page impact summary for the board",
];

export function ImpactStrip() {
  const [year, setYear] = useState<number | undefined>(undefined);
  const [d, setD] = useState<Impact | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Card>(null);
  const [cut, setCut] = useState<"month" | "heard" | "zip">("month");

  useEffect(() => { os.impact(year).then(setD).catch((e) => setErr(e instanceof Error ? e.message : String(e))); }, [year]);
  // C51: a headcount saved from the "how many women?" ask or the Ask bar lands here without a reload.
  useEffect(() => onImpact((i) => setD((cur) => (!cur || cur.year === i.year ? i : cur))), []);
  useEffect(() => { setAskContext({ page: open === "report" ? "report" : "today" }); }, [open]);

  if (err) return <div className="refusebox">The impact numbers could not load: {err}</div>;
  if (!d) return <section className="imp"><h2 className="sec">Impact this year</h2><p className="muted">Counting…</p></section>;

  const toggle = (c: Card) => setOpen((o) => (o === c ? null : c));
  const people = (rows: ImpactRow[], empty: string) => rows.length === 0
    ? <p className="imp-empty">{empty}</p>
    : <ul className="imp-rows">{rows.map((r, i) => (
        <li key={(r.id ?? "anon") + i}>
          {r.id ? <a href={`#people/${r.id}`}>{r.name}</a> : <span>{r.name}</span>}
          <span>{[r.how, r.on && day(r.on)].filter(Boolean).join(" · ")}</span>
        </li>))}</ul>;
  const chips = (list: Chip[]) => list.length > 0 && (
    <div className="imp-chips">{list.map((c) => <span key={c.label}>{c.label} {c.n}</span>)}</div>);
  const nothing = `Nothing counted yet for ${d.year}.`;

  return (
    <section className="imp">
      <div className="imp-head">
        <h2 className="sec">Impact this year</h2>
        <select aria-label="Year" value={d.year} onChange={(e) => setYear(Number(e.target.value))}>
          {d.years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="imp-grid">
        <button className={"imp-card w" + (open === "connected" ? " on" : "")} onClick={() => toggle("connected")}>
          <b>{d.connected.count}</b><span className="l">Women connected</span>
          <span className="s">{sinceLine(d.connected.since, d.year, "nothing counted yet")}</span>
          <span className="t">Tap to see who ›</span>
        </button>
        <button className={"imp-card w" + (open === "referred" ? " on" : "")} onClick={() => toggle("referred")}>
          <b>{d.referred.count}</b><span className="l">Women referred to other services</span>
          <span className="s">{sinceLine(d.referred.since, d.year, "starts with the first referral you log")}</span>
          <span className="t">Tap to see who ›</span>
        </button>
        <button className={"imp-card" + (open === "students" ? " on" : "")} onClick={() => toggle("students")}>
          <b>{d.students.count}</b><span className="l">Students and alumni</span>
          <span className="s">{d.students.academies} {d.students.academies === 1 ? "Academy" : "Academies"} so far</span>
          <span className="t">Tap to see who ›</span>
        </button>
        <button className={"imp-card n" + (open === "workshops" ? " on" : "")} onClick={() => toggle("workshops")}>
          <b>{d.workshops.count}</b><span className="l">Women at workshops</span>
          <span className="s">pop-ups and online, not the Academy</span>
          <span className="t">Tap to see who ›</span>
        </button>
        {/* C50, D-0085: women reached in the room. A headcount, kept apart from the four named cards. */}
        <button className={"imp-card imp-reach" + (open === "reached" ? " on" : "")} onClick={() => toggle("reached")}>
          <b>{d.reached.count}</b><span className="l">Women reached at outreach</span>
          <span className="s">{d.reached.sessions === 0 ? "log a talk or workshop to start counting"
            : `${d.reached.sessions} ${d.reached.sessions === 1 ? "talk or workshop" : "talks and workshops"}, a headcount in the room`
              + (d.reached.estimated > 0 ? `, ${d.reached.estimated} estimated` : "")}</span>
          <span className="t">Tap to log or see ›</span>
        </button>
      </div>

      {open === "reached" && (
        <div className="imp-panel">
          <h3>{d.reached.count} women reached in {d.year}</h3>
          <p className="imp-note">How many women were in the room when you spoke. No names, so the same woman at two talks counts twice. Kept apart from the women you serve.</p>
          <OutreachQuick onSaved={setD} />
          {chips(d.reached.by_place)}
          {d.reached.rows.length === 0 ? <p className="imp-empty">{nothing}</p> : (
            <ul className="imp-rows">{d.reached.rows.map((r) => (
              <li key={r.id}><span>{r.place} · {r.kind}</span>
                <span>{r.n} women{r.estimated ? " (estimate)" : ""} · {day(r.on)}
                  {r.today && <button className="imp-undo" onClick={() => void os.outreachUndo(r.id).then(setD)}>Undo</button>}</span></li>))}</ul>)}
        </div>
      )}

      {open === "connected" && (
        <div className="imp-panel">
          <h3>{d.connected.count} {d.connected.count === 1 ? "woman" : "women"} connected in {d.year}</h3>
          <p className="imp-note">Signed up, called in, referred, or at a workshop. Each woman counted once.</p>
          <div className="imp-cuts">
            {(["month", "heard", "zip"] as const).map((k) => (
              <button key={k} className={cut === k ? "on" : ""} onClick={() => setCut(k)}>
                {k === "month" ? "By month" : k === "heard" ? "How they found us" : "By ZIP code"}</button>))}
          </div>
          {chips(cut === "month" ? d.connected.by_month : cut === "heard" ? d.connected.by_heard : d.connected.by_zip)}
          {people(d.connected.people, nothing)}
        </div>
      )}

      {open === "referred" && (
        <div className="imp-panel">
          <h3>{d.referred.referrals} {d.referred.referrals === 1 ? "referral" : "referrals"} in {d.year}
            {d.referred.referrals !== d.referred.count && ` for ${d.referred.count} women`}</h3>
          <ReferralQuick onSaved={(imp) => setD(imp)} />
          {chips(d.referred.by_agency)}
          {d.referred.rows.length === 0 ? <p className="imp-empty">{nothing}</p> : (
            <ul className="imp-rows">{d.referred.rows.map((r) => (
              <li key={r.id}>
                {r.person_id ? <a href={`#people/${r.person_id}`}>{r.name}</a> : <span>{r.name}</span>}
                <span>→ {r.agency} · {day(r.on)}
                  {r.today && <button className="imp-undo" onClick={() => void os.referralUndo(r.id).then(setD)}>Undo</button>}</span>
              </li>))}</ul>)}
          <p className="imp-note">A caller with no name counts as one woman each time, because she cannot be matched.</p>
        </div>
      )}

      {open === "students" && (
        <div className="imp-panel">
          <h3>{d.students.count} students and alumni</h3>
          {chips(d.students.by_cohort)}
          {people(d.students.people, "No Academy students are entered in the database yet.")}
        </div>
      )}

      {open === "workshops" && (
        <div className="imp-panel">
          <h3>{d.workshops.count} {d.workshops.count === 1 ? "woman" : "women"} at workshops in {d.year}</h3>
          {d.workshops.count > 0 && chips([{ label: "pop-up", n: d.workshops.popup }, { label: "online", n: d.workshops.online },
            { label: "came back 2+ times", n: d.workshops.repeat }])}
          {people(d.workshops.people, "No workshop attendance is recorded yet.")}
        </div>
      )}

      <div className="imp-report">
        <div><b>Need a report for a funder or the board?</b><span>Say what they asked for. You get a document back to download.</span></div>
        <button onClick={() => toggle("report")}>Get a report</button>
      </div>
      {open === "report" && (
        <div className="imp-panel">
          <h3>What do you need?</h3>
          <AskClaude suggestions={REPORT_IDEAS} placeholder="Tell me what the funder wants" />
        </div>
      )}
    </section>
  );
}
